import {
    useStorageAsync,
    type MaybeRefOrGetter,
    type RemovableRef,
    type UseStorageOptions,
} from '@vueuse/core';
import {capacitorStorage} from './capacitorStorage';
import {appConfig} from '@/config/appConfig';

/**
 * Provides a composable for accessing device storage.
 *
 * Depending on the application configuration, this composable will use either
 * Capacitor storage (for hybrid apps) or the browser's localStorage.
 *
 * @template T - The type of the value to be stored.
 * @param key - The storage key to use.
 * @param initialValue - The initial value to store if the key does not exist.
 * @param options - Optional configuration for storage behavior.
 * @returns A `RemovableRef<T>` that is reactive and can be used to read, write, and remove the value from storage.
 */
export function useDeviceStorage<T>(
    key: string,
    initialValue: MaybeRefOrGetter<T>,
    options?: UseStorageOptions<T>
): RemovableRef<T> {
    const storage = appConfig.isHybridApp ? capacitorStorage : localStorage;
    return useStorageAsync(key, initialValue, storage, options) as RemovableRef<T>;
}
